import { authFetch } from './auth';
import { normalizeTelemetryPath, trackProductEvent } from './telemetry';

export type CaregiverData = Record<string, string | boolean | number | null | undefined | object>;

export interface CaregiverDocument {
  id: string;
  field: string;
  name: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
}

export interface GeneratedDocument {
  id: number;
  documentName: string;
  formName: string | null;
  createdAt: string;
}

export type DocumentSet = 'application' | 'onboarding' | 'completed';
export type SubmissionProcessingState = 'pending' | 'processing' | 'completed' | 'failed';

export interface CaregiverSubmissionStatus {
  form: 'application' | 'onboarding';
  status: SubmissionProcessingState;
  attempts: number;
  documentCount: number;
  completedAt: string | null;
}

export interface ClientEventPayload {
  level?: 'info' | 'warn' | 'error';
  context: string;
  details?: Record<string, unknown>;
}

export interface GenerateDocumentsOptions {
  documentSet?: DocumentSet;
  templateNames?: string[];
  singleDoc?: string;
  saveToDB?: boolean;
}

async function parseJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || 'Request failed');
  }
  return data as T;
}

export async function getCaregiver(): Promise<CaregiverData> {
  const response = await authFetch('/api/caregiver');
  const data = await parseJson<{ caregiver: CaregiverData }>(response);
  return data.caregiver;
}

export async function saveCaregiver(payload: CaregiverData): Promise<CaregiverData> {
  const response = await authFetch('/api/caregiver', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await parseJson<{ caregiver: CaregiverData; nexusWarning?: string | null }>(response);
  return data.caregiver;
}

export async function saveSignature(form: 'application' | 'onboarding', dataUrl: string): Promise<void> {
  const response = await authFetch('/api/caregiver/signature', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ form, dataUrl }),
  });
  await parseJson(response);
}

export async function submitCaregiver(
  form: 'application' | 'onboarding',
  fields: CaregiverData,
  signatureDataUrl?: string,
): Promise<{ caregiver: CaregiverData; submission: CaregiverSubmissionStatus }> {
  const response = await authFetch('/api/caregiver/submit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ form, fields, signatureDataUrl }),
  });
  return parseJson(response);
}

export async function getCaregiverSubmissionStatus(
  form: 'application' | 'onboarding',
): Promise<CaregiverSubmissionStatus> {
  const response = await authFetch(`/api/caregiver/submission-status?form=${encodeURIComponent(form)}`);
  const data = await parseJson<{ submission: CaregiverSubmissionStatus }>(response);
  return data.submission;
}

export async function uploadDocument(field: string, name: string, dataUrl: string): Promise<CaregiverDocument> {
  const response = await authFetch('/api/caregiver/documents', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ field, name, dataUrl }),
  });
  const data = await parseJson<{ document: CaregiverDocument }>(response);
  return data.document;
}

export async function getSummaryPdf(form: 'application' | 'onboarding'): Promise<Blob> {
  const response = await authFetch(`/api/caregiver/documents/summary?form=${form}`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || 'Failed to generate summary PDF');
  }
  return response.blob();
}

export async function getTemplatePdf(
  templateName: string,
  options: { formName?: string; language?: string } = {}
): Promise<Blob> {
  const params = new URLSearchParams({
    formName: options.formName ?? 'caregiver',
    language: options.language ?? 'English',
  });
  const response = await authFetch(`/api/templates/byName/${encodeURIComponent(templateName)}?${params}`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || 'Failed to load template');
  }
  return response.blob();
}

export async function generateDocuments(options: GenerateDocumentsOptions = {}): Promise<{
  documents: GeneratedDocument[];
  inlineDocs?: Array<{ docName: string; documentName: string; pdfBase64: string }>;
  missingTemplates: string[];
  nexusUploadWarnings?: string[];
}> {
  const response = await authFetch('/api/documents/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ documentSet: 'application', ...options }),
  });
  return parseJson(response);
}

export async function generateApplicationDocuments() {
  return generateDocuments({ documentSet: 'application' });
}

export async function sendSubmissionEmail(mailType: 'app submitted' | 'review' | 'completed'): Promise<{
  message: string;
  attachedDocuments: string[];
}> {
  const response = await authFetch('/api/documents/submission-email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mailType }),
  });
  return parseJson(response);
}

export async function reportClientEvent(payload: ClientEventPayload): Promise<void> {
  trackProductEvent('portal_client_event', {
    level: payload.level,
    context: payload.context,
    path: normalizeTelemetryPath(window.location.pathname),
  });
  try {
    await authFetch('/api/client-events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...payload,
        path: window.location.pathname,
      }),
    });
  } catch (eventError) {
    console.warn('[ClientEvent] Failed to report client event:', eventError);
  }
}

export async function listGeneratedDocuments(documentSet: DocumentSet = 'application'): Promise<GeneratedDocument[]> {
  const response = await authFetch(`/api/documents/my?documentSet=${encodeURIComponent(documentSet)}`);
  const data = await parseJson<{ documents: GeneratedDocument[] }>(response);
  return data.documents;
}

export async function getGeneratedDocumentPdf(documentId: number): Promise<Blob> {
  const response = await authFetch(`/api/documents/${documentId}`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || 'Failed to load document');
  }
  return response.blob();
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
